/**
 * Os recursos compartilhados — telefone, e-mail, endereço, rede social — como cadastro próprio.
 *
 * Estas quatro tabelas são deduplicadas, e é nelas que a correlação nasce: duas pessoas com o mesmo
 * telefone não são duas linhas parecidas, são o MESMO registro apontado por duas. Até aqui só dava
 * para chegar ao telefone partindo da pessoa; agora dá para partir do telefone e ver quem o usa.
 *
 * Os quatro nascem de uma fábrica porque são estruturalmente idênticos — quatro descritores
 * copiados divergiriam no primeiro ajuste que alguém fizesse em só um deles.
 *
 * **Os vínculos aparecem na LISTA**, como ícones com contagem: `👤1 🏢2`. É essa informação que faz
 * alguém querer abrir uma linha específica entre 175 mil; sem ela, a lista é um paginador cego.
 */
import { AtSign, Building2, Home, type LucideIcon, MapPin, Phone, Share2, User } from 'lucide-react';
import { PainelQuemUsa } from '@/components/crud/paineis/PainelQuemUsa';
import { definirRecurso, type RecursoBase } from '@/lib/recursos/tipos';

/** O que a lista devolve: o rótulo pronto e a contagem por tipo de dono. */
type Auxiliar = RecursoBase & {
  rotulo: string;
  vinculos: Record<string, number>;
  total_vinculos: number;
};

/** Ícone por tipo de dono — «1 Pessoa, 2 Empresas» dito em símbolo, não em palavra. */
const ICONE_DONO: Record<string, { icone: LucideIcon; titulo: string }> = {
  pessoa: { icone: User, titulo: 'pessoas' },
  empresa: { icone: Building2, titulo: 'empresas' },
  condominio: { icone: Home, titulo: 'condomínios' },
  imovel: { icone: MapPin, titulo: 'imóveis' },
};

/** A coluna de vínculos: um par ícone+número por tipo, e um traço quando não há nenhum. */
function Vinculos({ item }: { item: Auxiliar }) {
  const entradas = Object.entries(item.vinculos ?? {}).filter(([, n]) => n > 0);
  if (!entradas.length) {
    return <span className="text-text-tertiary">—</span>;
  }
  return (
    <span className="flex items-center gap-3">
      {entradas.map(([tipo, n]) => {
        const def = ICONE_DONO[tipo];
        if (!def) return null;
        const Icone = def.icone;
        // `title` porque ícone sozinho é adivinhação para quem chega na tela pela primeira vez.
        return (
          <span
            key={tipo}
            title={`${n} ${def.titulo}`}
            className="flex items-center gap-1 text-text-secondary"
          >
            <Icone className="size-3.5 shrink-0" />
            <span className="tipo-dado">{n}</span>
          </span>
        );
      })}
    </span>
  );
}

function auxiliar(conf: {
  chave: string;
  recurso: string;
  titulo: string;
  tituloSingular: string;
  descricao: string;
  icone: LucideIcon;
  buscaPlaceholder: string;
  /** Colunas próprias do recurso, além de rótulo e vínculos. */
  extras?: { chave: string; rotulo: string; ocultarEm?: 'sm' | 'md' }[];
}) {
  return definirRecurso<Auxiliar>({
    chave: conf.chave,
    endpoint: `/cadastro/auxiliares/${conf.recurso}`,
    titulo: conf.titulo,
    tituloSingular: conf.tituloSingular,
    descricao: conf.descricao,
    icone: conf.icone,
    grupo: 'Auxiliares',
    // Recurso compartilhado nasce do vínculo: criar um telefone solto não liga nada a ninguém, e
    // apagar um arrancaria a correlação de todos os donos de uma vez.
    capacidades: { podeCriar: false, podeEditar: false, podeExcluir: false },
    buscaPlaceholder: conf.buscaPlaceholder,
    rotuloItem: (i) => i.rotulo,
    colunas: [
      { chave: 'rotulo', rotulo: conf.tituloSingular, mono: true },
      ...(conf.extras ?? []).map((e) => ({
        chave: e.chave as keyof Auxiliar & string,
        rotulo: e.rotulo,
        ocultarEm: e.ocultarEm,
      })),
      {
        chave: 'total_vinculos',
        rotulo: 'Vínculos',
        render: (item) => <Vinculos item={item} />,
      },
    ],
    camposCriar: [],
    gruposEdicao: [],
    telaEdicaoCustom: ({ item }) => (
      <PainelQuemUsa recurso={conf.recurso} item={item as Auxiliar} icone={conf.icone} />
    ),
  });
}

export const telefoneAux = auxiliar({
  chave: 'aux-telefones',
  recurso: 'telefones',
  titulo: 'Telefones',
  tituloSingular: 'telefone',
  descricao: 'Cada número existe uma vez. Quem aparece em dois donos é correlação, não coincidência.',
  icone: Phone,
  buscaPlaceholder: 'Buscar por número (com ou sem formatação)',
  extras: [{ chave: 'tipo' as never, rotulo: 'Tipo', ocultarEm: 'md' }],
});

export const emailAux = auxiliar({
  chave: 'aux-emails',
  recurso: 'emails',
  titulo: 'E-mails',
  tituloSingular: 'e-mail',
  descricao: 'Endereços de e-mail deduplicados e quem os utiliza.',
  icone: AtSign,
  buscaPlaceholder: 'Buscar por endereço de e-mail',
});

export const enderecoAux = auxiliar({
  chave: 'aux-enderecos',
  recurso: 'enderecos',
  titulo: 'Endereços',
  tituloSingular: 'endereço',
  descricao: 'Endereços deduplicados, com coordenada quando conhecida, e seus ocupantes.',
  icone: MapPin,
  buscaPlaceholder: 'Buscar por logradouro, bairro, cidade ou CEP',
  extras: [
    { chave: 'cidade' as never, rotulo: 'Cidade', ocultarEm: 'sm' },
    { chave: 'cep' as never, rotulo: 'CEP', ocultarEm: 'md' },
  ],
});

export const redeSocialAux = auxiliar({
  chave: 'aux-redes',
  recurso: 'redes-sociais',
  titulo: 'Redes sociais',
  tituloSingular: 'perfil',
  descricao: 'Perfis deduplicados por plataforma e identificador.',
  icone: Share2,
  buscaPlaceholder: 'Buscar por identificador ou plataforma',
  extras: [{ chave: 'plataforma' as never, rotulo: 'Plataforma', ocultarEm: 'sm' }],
});
